import { DepotKind, NO_ID, TrainFlags, UnitFlags, type DesignInfo } from "../replay/format";
import { industry } from "../state/industry";
import { sitesByStation } from "../state/structures";
import { siteDone, type Site } from "../console/BuildTab";
import type { WorldState } from "../state/world";
import { REGION } from "../replay/replay";
import { Camera } from "./camera";
import { rgb, type GL } from "./gl";
import { Ground, Layer, OverviewMode, groundFor } from "./ground";
import { SQRT3, hexAt, hexX, hexY, slot } from "./hex";
import { LineStyle, Lines } from "./lines";
import { Part, Shape, SpriteFlag, Sprites } from "./sprites";
import { TitanRig } from "./titans";
import { oreField, type OreJob, type OreTexture } from "./oreField";
import { settings } from "../ui/settings";
import { ORE_UNKNOWN, TINT, oreRGB, oresOf, metric, mixRGB, saturate, tintOf, type Kind, type Mark, type RGB, type Sat } from "./colour";

/** A prospected cell's findings, as the heatmap reads them. */
export interface OreFinding {
  cell: number;
  total: [number, number] | null;
  ores: { ore: string; amount: number; error_pct: number }[];
  /** A deep core's per-hex grade (0-255) of each ore present, row-major
   *  from the cell's corner; soil samples' per 4x4 sub-cell. */
  grid?: Record<string, number[]>;
  grid4?: Record<string, number[]>;
}

/** The heatmap's worker (one, shared; null where workers aren't). */
let oreWorkerOne: Worker | null | undefined;
function oreWorker(): Worker | null {
  if (oreWorkerOne === undefined) {
    try {
      oreWorkerOne = new Worker(new URL("./oreWorker.ts", import.meta.url), { type: "module" });
    } catch {
      oreWorkerOne = null;
    }
  }
  return oreWorkerOne;
}

/** Per-hex working arrays (armies, sight, heat), kept across renderers of
 *  the same size: a 2048-hex map's are ~200 MB, and a renderer is built
 *  every time the world is sent again. */
let tilePool: { tiles: number; dirty: boolean; arrays: Record<string, ArrayBufferView & { fill(v: number): unknown }> } | null = null;
/** The world's extent inside its storm wall, per region layer (a scan of
 *  every hex: worth keeping across renderers of the same map). */
const boundsOf = new WeakMap<Uint8Array, [number, number, number, number] | null>();
function tileArrays(tiles: number): Record<string, ArrayBufferView & { fill(v: number): unknown }> {
  if (tilePool && tilePool.tiles === tiles) {
    // Cleared once per renderer (the second ask in a constructor is free).
    if (tilePool.dirty) {
      for (const [k, a] of Object.entries(tilePool.arrays)) a.fill(k === "head" ? -1 : 0);
      tilePool.dirty = false;
    }
    return tilePool.arrays;
  }
  tilePool = null;
  const arrays = {
    stand: new Uint8Array(tiles),
    c0: new Uint8Array(tiles),
    c1: new Uint8Array(tiles),
    c2: new Uint8Array(tiles),
    c3: new Uint8Array(tiles),
    hpSum: new Uint16Array(tiles),
    ammoSum: new Uint16Array(tiles),
    tintSum: new Float32Array(tiles * 4),
    tint: new Uint8Array(tiles * 4),
    occ: new Uint8Array(tiles * 4),
    own: new Uint8Array(tiles),
    status: new Uint8Array(tiles * 4),
    heat: new Float32Array(tiles),
    vis: new Uint8Array(tiles),
    seen: new Uint8Array(tiles),
    head: new Int32Array(tiles),
  };
  arrays.head.fill(-1);
  tilePool = { tiles, dirty: false, arrays };
  return arrays;
}

export interface RenderOptions {
  /** Team hue always, tinted by a categorical state; an analog measure as
   *  saturation. */
  mark: Mark;
  sat: Sat;
  units: boolean;
  fire: boolean;
  shells: boolean;
  trains: boolean;
  depots: boolean;
  track: boolean;
  corridors: boolean;
  zones: boolean;
  grid: boolean;
  effects: boolean;
  minimap: boolean;
  /** Settled ground (live commanders). */
  territory: boolean;
  /** Prospecting findings (live commanders): the ore heatmap. */
  intel: boolean;
  /** Which ore the heatmap shows ("All", or an ore's name). */
  ore: string;
  /** Goods on their way between a commander's stations, as arrows. */
  flows: boolean;
  /** "Station full" tags on the map (other reasons always show). */
  fullWarnings: boolean;
  /** Prospecting samples' rings and time left (always, with prospectors
   *  selected). */
  samples: boolean;
  /** Bit per faction to show (units, corridors), for factions 0-7; later
   * ones always show (a world of many factions filters some other way). */
  factions: number;
  /** Fog of war from this side's sight (-1: everything shows). */
  sight: number;
}

/** Whether faction `f` shows under `o`'s faction filter. */
export const shows = (o: RenderOptions, f: number): boolean => f >= 8 || ((o.factions >> f) & 1) === 1;

const WHITE: [number, number, number] = [1, 1, 1];

export const defaultOptions = (): RenderOptions => ({
  mark: "dry",
  sat: "health",
  units: true,
  fire: true,
  shells: true,
  trains: true,
  depots: true,
  track: true,
  corridors: false,
  zones: false,
  grid: true,
  effects: true,
  minimap: true,
  territory: true,
  intel: true,
  ore: "All",
  flows: true,
  fullWarnings: false,
  samples: false,
  factions: 0xff,
  sight: -1,
});

interface Effect {
  x: number;
  y: number;
  tick: number;
  kind: "burst" | "death";
  faction: number;
}

/** What's under a screen point. */
export interface Pick {
  q: number;
  r: number;
  tile: number | null;
  unit: number | null;
}

export const MINIMAP = { width: 260, margin: 14 };

/** Which overview channel a saturation measure maps to (dug-in isn't
 *  aggregated per hex). */
const SAT_CHANNEL: Record<Sat, number> = { none: 0, health: 1, ammo: 2, suppression: 3, dug: 0 };

/** Draws a `WorldState` with WebGL2: ground and armies, then sprites and lines. */
export class Renderer {
  /** Where the page wants the minimap (CSS pixels, viewport): its console
   *  slot; null for the corner. */
  minimapSlot: { left: number; top: number; width: number; height: number } | null = null;
  readonly gl: GL;
  readonly camera = new Camera();
  options = defaultOptions();
  selected: number | null = null;
  /** Units selected for orders (live), highlighted like `selected`. */
  selection: Set<number> | null = null;
  hover: Pick | null = null;

  private ground: Ground;
  private units: Sprites;
  private overlay: Sprites;
  private structures: Sprites;
  private lines: Lines;
  /** Titans: drawn on a 2D canvas over the map (filled, shaded shapes). */
  private titanCanvas: HTMLCanvasElement;
  private titanCtx: CanvasRenderingContext2D;
  private titans = new TitanRig();
  private factionColors: [number, number, number][];
  /** Each side's own colour (allies are drawn lifted from it). */
  private baseColors: [number, number, number][] = [];
  private allies = "";
  private designs: DesignInfo[];
  private shapes: Shape[];
  /** Each design's module marks (`Part`). */
  private parts: number[];
  private kinds: Kind[];
  /** Per hex: summed tint hues of tinted units (rgb) and their count. */
  private tintSum: Float32Array;
  private tint: Uint8Array;
  /** Per hex: summed ammo percent (unarmed units count as full). */
  private ammoSum: Uint16Array;
  private radii: number[];

  // Per-frame derived data.
  private posX: Float32Array;
  private posY: Float32Array;
  private prevX: Float32Array;
  private prevY: Float32Array;
  private placedTile: Uint32Array;
  private occ: Uint8Array;
  /** Under fog, the viewer's own fill per hex (one byte each). */
  private own: Uint8Array;
  private status: Uint8Array;
  private heat: Float32Array;
  private heatTiles = new Set<number>();
  // Per-hex unit lists as typed arrays (millions of hexes and units).
  private head: Int32Array;
  private next: Int32Array;
  private stand: Uint8Array;
  private slotIdx: Uint8Array;
  private touched: Int32Array;
  private touchedCount = 0;
  /** The corridor layer's version last uploaded. */
  private corridorsAt = -1;
  /** Every hex armies (or deaths) have touched since the last seek, as a
   *  box [q0, r0, q1, r1]: textures change only inside it, so only it is
   *  uploaded (the textures swap, so a frame's can be two frames stale). */
  private dirty: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];
  /** Likewise every hex fog has seen or is seeing since it was reset. */
  private sightBox: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];
  private counts: Uint8Array[];
  private hpSum: Uint16Array;
  private effects: Effect[] = [];
  /** Fog of war, per hex: 255 in sight, 115 seen before, 0 never (`sight`). */
  private vis: Uint8Array;
  private seen: Uint8Array;
  private seenFor = -1;
  private worldBounds: [number, number, number, number];

  constructor(
    readonly canvas: HTMLCanvasElement,
    readonly state: WorldState,
  ) {
    const gl = canvas.getContext("webgl2", { antialias: false, alpha: false, premultipliedAlpha: false });
    if (!gl) throw new Error("WebGL2 isn't available in this browser.");
    this.gl = gl;
    const h = state.header;
    // Kept across the world being sent again (a structure built): only
    // track, corridors and regions refresh, not the whole land.
    this.ground = groundFor(gl, state.width, state.height, state.statics, h.world.zone_size);
    this.units = new Sprites(gl);
    this.overlay = new Sprites(gl);
    this.structures = new Sprites(gl);
    this.lines = new Lines(gl);
    // One layer at a time: a renderer before this one leaves its own.
    for (const old of document.querySelectorAll(".titan-layer")) old.remove();
    this.titanCanvas = document.createElement("canvas");
    this.titanCanvas.className = "titan-layer";
    canvas.after(this.titanCanvas);
    this.titanCtx = this.titanCanvas.getContext("2d")!;
    this.factionColors = h.factions.map((f) => rgb(f.color));
    this.baseColors = this.factionColors.map((c) => [...c] as [number, number, number]);
    this.designs = h.designs;
    this.shapes = h.designs.map((d) => {
      // Storm creatures (v31.13): their own jagged glyph.
      if (/^Storm (crawler|brute)$/.test(d.name)) return Shape.Storm;
      // The body is the chassis; older replays (no chassis) guess from stats.
      const body: Record<string, Shape> = { Foot: Shape.Infantry, Tracked: Shape.Tank, Wheeled: Shape.Truck, Towed: Shape.Mortar };
      // A fixed gun (a guard post): it never moves.
      if (d.speed === 0 && d.damage > 0) return Shape.Post;
      // Robot infantry: chassis "Robot" (a machine) since v29.73, a robot body on foot before.
      if (d.chassis === "Robot" || (d.chassis === "Foot" && d.body === "Robot")) return Shape.Robot;
      if (d.chassis && d.chassis in body) return body[d.chassis];
      return d.carrier ? Shape.Truck : d.indirect ? Shape.Mortar : d.damage === 0 ? Shape.Unit : d.size >= 4 ? Shape.Tank : d.size <= 1 ? ((d.pen ?? 0) >= 30 ? Shape.AntiTank : Shape.Infantry) : Shape.Unit;
    });
    this.parts = h.designs.map((d, i) => {
      const mods = d.modules ?? [];
      const has = (k: string) => mods.some((m) => m.kind === k);
      const foot = this.shapes[i] === Shape.Infantry || this.shapes[i] === Shape.AntiTank || this.shapes[i] === Shape.Robot;
      const weapon = mods.find((m) => m.kind === "weapon");
      let parts = 0;
      if (weapon && (weapon.pen ?? 0) >= 30) parts |= foot ? Part.ANTI_ARMOUR : Part.GUN;
      if (has("mortar") || (!d.modules && d.indirect)) parts |= Part.MORTAR;
      if (has("armor") || (!d.modules && (d.armor ?? 0) > 0)) parts |= Part.ARMOUR;
      if (has("cargo") || (!d.modules && d.carrier)) parts |= Part.CARGO;
      // Builders and prospectors carry their tool's mark, not a hollow.
      if (has("construction")) parts |= Part.BUILDER;
      else if (has("prospector")) parts |= Part.SURVEY;
      else if (d.modules && !weapon && !has("mortar") && !has("cargo")) parts |= Part.UNARMED;
      return parts;
    });
    this.kinds = this.shapes.map((sh, i) =>
      sh === Shape.Tank ? "tank" : sh === Shape.Truck ? "truck" : sh === Shape.Mortar ? "mortar" : sh === Shape.AntiTank || this.parts[i] & Part.ANTI_ARMOUR ? "anti-tank" : h.designs[i].damage === 0 ? "prisoners" : "infantry",
    );

    this.radii = h.designs.map((d) => (d.size >= 4 ? 0.54 : d.size >= 2 ? 0.36 : 0.29));
    const n = h.unit_ids;
    this.posX = new Float32Array(n);
    this.posY = new Float32Array(n);
    this.prevX = new Float32Array(n);
    this.prevY = new Float32Array(n);
    this.placedTile = new Uint32Array(n).fill(NO_ID);
    this.head = tileArrays(state.tiles).head as Int32Array;
    this.next = new Int32Array(n);
    // The map-sized ones from the pool (the last renderer's, cleared).
    const pool = tileArrays(state.tiles);
    this.stand = pool.stand as Uint8Array;
    this.slotIdx = new Uint8Array(n);
    this.touched = new Int32Array(n);
    this.counts = [pool.c0, pool.c1, pool.c2, pool.c3] as Uint8Array[];
    this.hpSum = pool.hpSum as Uint16Array;
    this.ammoSum = pool.ammoSum as Uint16Array;
    this.tintSum = pool.tintSum as Float32Array;
    this.tint = pool.tint as Uint8Array;
    this.occ = pool.occ as Uint8Array;
    this.own = pool.own as Uint8Array;
    this.status = pool.status as Uint8Array;
    this.heat = pool.heat as Float32Array;
    this.vis = pool.vis as Uint8Array;
    this.seen = pool.seen as Uint8Array;
    const [w, ht] = [state.width, state.height];
    // The map is a parallelogram: x runs from hex (0, 0) to hex (w-1, h-1).
    this.worldBounds = [hexX(0, 0) - 1, hexY(0) - 1, hexX(w - 1, ht - 1) + 1, hexY(ht - 1) + 1];
    // A world with a storm wall: the world is what isn't void (the canvas
    // round it is mostly nothing), for the whole-map view and the minimap.
    const region = state.statics.region;
    const known = region ? boundsOf.get(region) : undefined;
    if (region && known !== undefined) {
      if (known) this.worldBounds = known;
    } else if (region) {
      let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
      for (let r = 0; r < ht; r++) {
        const row = r * w;
        let lo = -1;
        let hi = -1;
        for (let q = 0; q < w; q++) {
          if (region[row + q] !== REGION.VOID) {
            if (lo < 0) lo = q;
            hi = q;
          }
        }
        if (lo < 0) continue;
        x0 = Math.min(x0, hexX(lo, r));
        x1 = Math.max(x1, hexX(hi, r));
        y0 = Math.min(y0, hexY(r));
        y1 = Math.max(y1, hexY(r));
      }
      if (x0 <= x1) this.worldBounds = [x0 - 1, y0 - 1, x1 + 1, y1 + 1];
      boundsOf.set(region, x0 <= x1 ? this.worldBounds : null);
    }
  }

  /** Fog of war: what the `sight` side's units (and structures) see now,
   *  and what they've seen since the last seek. */
  private updateSight(jump: boolean): void {
    const side = this.options.sight;
    if (side < 0) return;
    const s = this.state;
    const { vis, seen } = this;
    const w = s.width;
    const h = s.height;
    // Reset: everything cleared and uploaded once, the box starting over.
    let full = false;
    if (jump || side !== this.seenFor) {
      seen.fill(0);
      vis.fill(0);
      full = true;
      this.sightBox = [Infinity, Infinity, -Infinity, -Infinity];
    }
    this.seenFor = side;
    const sb = this.sightBox;
    // Only within what's been seen: the rest stays 0.
    for (let r = Math.max(0, sb[1]); r <= Math.min(h - 1, sb[3]); r++) {
      for (let q = Math.max(0, sb[0]); q <= Math.min(w - 1, sb[2]); q++) vis[r * w + q] = seen[r * w + q] ? 115 : 0;
    }
    const stamp = (tile: number, radius: number) => {
      const q0 = tile % w;
      const r0 = (tile / w) | 0;
      if (q0 - radius < sb[0]) sb[0] = Math.max(0, q0 - radius);
      if (r0 - radius < sb[1]) sb[1] = Math.max(0, r0 - radius);
      if (q0 + radius > sb[2]) sb[2] = Math.min(w - 1, q0 + radius);
      if (r0 + radius > sb[3]) sb[3] = Math.min(h - 1, r0 + radius);
      for (let dr = -radius; dr <= radius; dr++) {
        const r = r0 + dr;
        if (r < 0 || r >= h) continue;
        const lo = Math.max(-radius, -dr - radius);
        const hi = Math.min(radius, -dr + radius);
        const row = r * w;
        for (let dq = lo; dq <= hi; dq++) {
          const q = q0 + dq;
          if (q < 0 || q >= w) continue;
          vis[row + q] = 255;
          seen[row + q] = 1;
        }
      }
    };
    // Their own, and the sides sharing sight with them (allies, agreements).
    const sees = (f: number) => f === side || this.sightShared.has(f);
    for (const id of s.live) if (sees(s.faction[id])) stamp(s.tile[id], this.designs[s.design[id]].sight);
    for (const p of s.statics.producers) if (sees(p.faction)) stamp(p.tile, 3);
    for (const d of s.depots) if (sees(d.faction)) stamp(d.tile, 3);
    if (full || sb[0] > sb[2]) this.ground.setVisibility(vis);
    else this.ground.setVisibilityRect(vis, sb[0], sb[1], sb[2] - sb[0] + 1, sb[3] - sb[1] + 1);
  }

  /** The commander's construction sites, drawn as ghosts. */
  private sites: (Site & { owner: number })[] = [];

  setSites(sites: Site[], owner: number): void {
    this.sites = sites.map((s) => ({ ...s, owner }));
  }

  /** Settled ground from the commander's state (`state` message):
   * `settler` and `level` (0..1000) per cell. */
  setTerritory(t: { cols: number; rows: number; settler: number[]; level: number[] }, cell: number): void {
    const n = t.cols * t.rows;
    if (t.settler.length !== n || t.level.length !== n) return;
    const data = new Uint8Array(n * 4);
    for (let c = 0; c < n; c++) {
      const p = t.settler[c];
      const colour = this.factionColors[p];
      if (p === 255 || !colour) continue;
      const a = Math.min(1, t.level[c] / 1000);
      data[c * 4] = colour[0] * a * 255;
      data[c * 4 + 1] = colour[1] * a * 255;
      data[c * 4 + 2] = colour[2] * a * 255;
      data[c * 4 + 3] = a * 255;
    }
    this.ground.setTerritory(t.cols, t.rows, cell, data);
  }

  /** Hexes ("q,r") of structures being decommissioned: drawn grey while
   *  closed; other closed structures aren't drawn. */
  private decommissioning = new Set<string>();

  setDecommissions(at: [number, number][]): void {
    this.decommissioning = new Set(at.map(([q, r]) => `${q},${r}`));
  }

  /** Each station's industry sites (by depot index), for one glyph a
   *  structure. */
  private stationSites: Map<number, number[]> | null = null;

  /** Prospecting findings (the commander's state) and the cell grid. */
  private findings: { list: OreFinding[]; cols: number; rows: number; cell: number } | null = null;
  /** The ore the heatmap was last built for. */
  private oreShown = "";

  setFindings(list: OreFinding[], cols: number, rows: number, cell: number): void {
    // The same findings as last time (most states): no rebuild (the
    // heatmap's blur over a big map took a good part of a second).
    let sig = cols * 7 + rows * 13 + cell + list.length * 101;
    for (const f of list) {
      sig = (sig * 31 + f.cell + (f.grid ? 3 : 0) + (f.grid4 ? 5 : 0) + (f.total?.[0] ?? 0)) % 2147483647;
      for (const o of f.ores) sig = (sig * 17 + o.amount + o.error_pct) % 2147483647;
    }
    if (sig === this.findingsSig && this.findings) return;
    this.findingsSig = sig;
    this.findings = { list, cols, rows, cell };
    this.oreShown = "";
  }
  private findingsSig = -1;

  /** The ore heatmap: one smooth field over prospected ground, each ore in
   *  its atlas hue. Each cell's find (relative to the richest found) is
   *  spread between cell centres; where samples tell each hex's grade (a
   *  core's per hex, soil's per sixteenth of a cell) the cell's heat is
   *  shared out by it, so the rich ground inside a cell shows without
   *  seams; then a light blur. Richer is brighter and more opaque, little
   *  fades into the ground, ores together mix their hues (a double deposit
   *  reads as both); ore of a kind not known yet (an EM survey only) is a
   *  faint haze. */
  private buildOre(): void {
    const f = this.findings;
    const want = this.options.ore;
    this.oreShown = want;
    if (!f || !f.cols || !f.rows) return;
    const job: OreJob = { f, ores: oresOf(want), hues: oresOf(want).map(oreRGB), unknown: ORE_UNKNOWN, W: this.state.width, H: this.state.height };
    // In the worker (a big map's field took a tenth of a second, a stall
    // at every survey update); the latest job's answer is the one shown.
    const id = ++this.oreJob;
    const w = oreWorker();
    if (w) {
      w.onmessage = (e: MessageEvent<OreTexture & { id: number }>) => {
        if (e.data.id === this.oreJob) this.ground.setOre(e.data.TW, e.data.TH, e.data.res, e.data.data);
      };
      w.postMessage({ id, job });
      return;
    }
    const t = oreField(job);
    this.ground.setOre(t.TW, t.TH, t.res, t.data);
  }
  private oreJob = 0;

  /** A side's colour as CSS (the overlay's marks). */
  factionColour(f: number): string {
    const c = this.factionColors[f];
    return c ? `rgb(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)})` : "rgba(232,223,204,0.6)";
  }

  /** The emplacements in view this frame (comms posts and training camps
   *  stand as units since v31.1): where, whose, and which. */
  emplaced: { id: number; kind: "camp" | "post"; x: number; y: number; faction: number }[] = [];
  private emplaceKinds: ("camp" | "post" | null)[] = [];
  /** Whether design `d` is a comms post's or training camp's emplacement. */
  emplacementOf(d: number): "camp" | "post" | null {
    const k = this.emplaceKinds[d];
    if (k !== undefined) return k;
    const name = this.designs[d]?.name;
    const v = name === "Comms post" ? "post" : name === "Training camp" ? "camp" : null;
    this.emplaceKinds[d] = v;
    return v;
  }

  /** Whether a hex shows: in sight, with fog of war on. */
  inSight(tile: number): boolean {
    return this.options.sight < 0 || this.vis[tile] === 255;
  }

  /** Whether a hex has been seen (structures stay known once seen). */
  private known(tile: number): boolean {
    return this.options.sight < 0 || this.vis[tile] > 0;
  }

  /** World rectangle covering the map. */
  bounds(): [number, number, number, number] {
    return this.worldBounds;
  }

  /** Designs whose soldiers are robots (the commander's registered ones,
   *  by spec): drawn angular. */
  setRobots(ids: number[]): void {
    for (const id of ids) {
      if (this.shapes[id] === Shape.Infantry || this.shapes[id] === Shape.AntiTank) this.shapes[id] = Shape.Robot;
    }
  }

  /** Sides whose sight the viewer's shares (allies, agreements granting
   *  sight): fog lifts round their units too. */
  private sightShared = new Set<number>();
  setSightShared(sides: number[]): void {
    this.sightShared = new Set(sides);
  }

  /** The commander's allies: drawn in their own colour lifted toward a
   *  pale friendly white, so they read as friends but still as theirs. */
  setAllies(sides: number[]): void {
    const key = sides.join(",");
    if (key === this.allies) return;
    this.allies = key;
    this.factionColors = this.baseColors.map((c, i) => (sides.includes(i) ? (c.map((v, k) => v + ([0.9, 0.96, 1][k] - v) * 0.35) as [number, number, number]) : c));
  }

  /** Fits the armies (alive units) rather than the whole map. */
  fitUnits(immediate = false): void {
    const s = this.state;
    if (s.live.length === 0) return this.fit(immediate);
    let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
    for (const id of s.live) {
      x0 = Math.min(x0, this.posX[id]);
      y0 = Math.min(y0, this.posY[id]);
      x1 = Math.max(x1, this.posX[id]);
      y1 = Math.max(y1, this.posY[id]);
    }
    this.camera.fit(x0 - 4, y0 - 4, x1 + 4, y1 + 4, 0.06, immediate);
  }

  fit(immediate = false): void {
    const [x0, y0, x1, y1] = this.worldBounds;
    this.camera.fit(x0, y0, x1, y1, 0.04, immediate);
  }

  /** The commander's foundries (tiles), drawn as such (their state says
   *  which producers are; others' look like barracks). */
  private foundries = new Set<number>();
  setFoundries(tiles: number[]): void {
    this.foundries = new Set(tiles);
  }

  /** Lets go of the page (the ground stays, kept for the next renderer). */
  dispose(): void {
    // The pooled per-hex arrays back to clean for the next renderer: only
    // the hexes this one wrote (its last frame's armies and the fading
    // deaths), not the whole map (a 2048-hex map's are ~200 MB to clear).
    // Sight (vis, seen) the next renderer resets itself on its first frame.
    if (tilePool && tilePool.arrays.head === this.head) {
      const { head, stand, touched, hpSum, ammoSum, occ, own, status: st, tintSum, tint, heat } = this;
      const [cAll, cArmed, cDry, cSupp] = this.counts;
      for (let k = 0; k < this.touchedCount; k++) {
        const t = touched[k];
        head[t] = -1;
        stand[t] = cAll[t] = cArmed[t] = cDry[t] = cSupp[t] = 0;
        hpSum[t] = ammoSum[t] = 0;
        own[t] = 0;
        for (let c = 0; c < 4; c++) occ[t * 4 + c] = st[t * 4 + c] = tintSum[t * 4 + c] = tint[t * 4 + c] = 0;
      }
      this.touchedCount = 0;
      for (const t of this.heatTiles) {
        heat[t] = 0;
        st[t * 4 + 3] = 0;
      }
      this.heatTiles.clear();
    } else if (tilePool) tilePool.dirty = true;
    this.sizeWatch?.disconnect();
    this.sizeWatch = null;
  }

  /** The canvas's size in CSS pixels, told by a ResizeObserver when it
   *  changes (reading it every frame forced a layout every frame). */
  private cssSize: [number, number] | null = null;
  private sizeWatch: ResizeObserver | null = null;

  resize(): void {
    if (!this.sizeWatch) {
      this.cssSize = [this.canvas.clientWidth, this.canvas.clientHeight];
      this.sizeWatch = new ResizeObserver(() => (this.cssSize = [this.canvas.clientWidth, this.canvas.clientHeight]));
      this.sizeWatch.observe(this.canvas);
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2) * settings.resolution;
    const w = Math.round(this.cssSize![0] * dpr);
    const h = Math.round(this.cssSize![1] * dpr);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    // The titans' layer matches the map's, whenever it doesn't (a new layer
    // starts at a canvas's default size).
    if (this.titanCanvas.width !== w || this.titanCanvas.height !== h) {
      this.titanCanvas.width = w;
      this.titanCanvas.height = h;
    }
    this.camera.resize(w, h);
    this.dprNow = w / Math.max(1, this.cssSize![0]);
  }

  /** Device pixels per CSS pixel, as of the last resize (read every frame,
   *  and asking the canvas its width each time forced a layout). */
  private dprNow = 0;
  get dpr(): number {
    if (!this.dprNow) this.dprNow = this.canvas.width / Math.max(1, this.canvas.clientWidth);
    return this.dprNow;
  }

  /** Recomputes positions, aggregates and textures after frames change.
   *  `jump`: the change was a seek, so nothing should appear to move. */
  rebuild(jump: boolean): void {
    const s = this.state;
    const live = s.live;
    const w = s.width;
    const { head, next, stand, slotIdx, touched } = this;
    const [cAll, cArmed, cDry, cSupp] = this.counts;
    const hpSum = this.hpSum;
    const ammoSum = this.ammoSum;
    const markMode = this.options.mark;
    const tintSum = this.tintSum;
    const tint = this.tint;
    const occ = this.occ;
    const own = this.own;
    // Fog of war keeps the viewer's own army apart, so the rest can fade.
    const mine = this.options.sight >= 0 ? this.options.sight : -1;
    const st = this.status;
    // Clear last frame's hexes only (the map can have millions).
    for (let k = 0; k < this.touchedCount; k++) {
      const t = touched[k];
      head[t] = -1;
      stand[t] = cAll[t] = cArmed[t] = cDry[t] = cSupp[t] = 0;
      hpSum[t] = ammoSum[t] = 0;
      occ[t * 4] = occ[t * 4 + 1] = occ[t * 4 + 2] = occ[t * 4 + 3] = 0;
      own[t] = 0;
      st[t * 4] = st[t * 4 + 1] = st[t * 4 + 2] = 0;
      for (let c = 0; c < 4; c++) tintSum[t * 4 + c] = tint[t * 4 + c] = 0;
    }
    // Per-hex lists (linked through `next`) and standing slots, in id order.
    let touchedCount = 0;
    for (let k = 0; k < live.length; k++) {
      const id = live[k];
      const t = s.tile[id];
      if (head[t] === -1) touched[touchedCount++] = t;
      next[id] = head[t];
      head[t] = id;
      // Titans walk over the rest on their own plane: no standing slot (a
      // hex holds four and a titan).
      if (!(s.flags[id] & UnitFlags.ROAD) && !this.designs[s.design[id]]?.footprint) slotIdx[id] = stand[t]++;
    }
    this.touchedCount = touchedCount;

    const place = (id: number, x: number, y: number) => {
      if (jump || this.placedTile[id] === NO_ID) {
        this.prevX[id] = x;
        this.prevY[id] = y;
      } else if (this.placedTile[id] === s.prevTile[id]) {
        this.prevX[id] = this.posX[id];
        this.prevY[id] = this.posY[id];
      } else {
        const pt = s.prevTile[id];
        const t = s.tile[id];
        this.prevX[id] = hexX(pt % w, (pt / w) | 0) + (x - hexX(t % w, (t / w) | 0));
        this.prevY[id] = hexY((pt / w) | 0) + (y - hexY((t / w) | 0));
      }
      this.posX[id] = x;
      this.posY[id] = y;
      this.placedTile[id] = s.tile[id];
    };
    for (let k = 0; k < live.length; k++) {
      const id = live[k];
      const t = s.tile[id];
      const q = t % w;
      const r = (t / w) | 0;
      const d = this.designs[s.design[id]];
      const f = s.faction[id];
      if (s.flags[id] & UnitFlags.ROAD) {
        // Road traffic: on the hex centre line, one lane each way.
        const lane = s.flags[id] & UnitFlags.RETURNING ? 0.16 : -0.16;
        place(id, hexX(q, r) + lane, hexY(r) + lane * 0.5);
      } else {
        // A titan at the hex's centre (it takes no slot); the rest in theirs.
        const [ox, oy] = d.footprint ? [0, 0] : slot(slotIdx[id], stand[t]);
        place(id, hexX(q, r) + ox, hexY(r) + oy);
        // Each hex's armies, blended here for any number of factions:
        // colour times fill (premultiplied, so mipmaps average right). A
        // titan (size 0: it takes no room) shades its hex in full.
        const fill = ((d.footprint ? 4 : d.size) / 4) * 255;
        if (f === mine) own[t] = Math.min(255, own[t] + fill);
        else if (shows(this.options, f)) {
          const c = this.factionColors[f] ?? WHITE;
          occ[t * 4] = Math.min(255, occ[t * 4] + c[0] * fill);
          occ[t * 4 + 1] = Math.min(255, occ[t * 4 + 1] + c[1] * fill);
          occ[t * 4 + 2] = Math.min(255, occ[t * 4 + 2] + c[2] * fill);
          occ[t * 4 + 3] = Math.min(255, occ[t * 4 + 3] + fill);
        }
      }
      cAll[t]++;
      if (d.damage > 0) {
        cArmed[t]++;


      }
      if (s.suppression[id] >= 5) cSupp[t]++;
      // Tint hues, for the overview.
      const th = tintOf(s, d, id, markMode, this.kinds[s.design[id]]);
      if (th) {
        tintSum[t * 4] += th[0];
        tintSum[t * 4 + 1] += th[1];
        tintSum[t * 4 + 2] += th[2];
        tintSum[t * 4 + 3]++;
      }
      hpSum[t] += Math.round((100 * s.hp[id]) / Math.max(1, d.hp));
      ammoSum[t] += d.ammo > 0 ? Math.round((100 * s.ammo[id]) / d.ammo) : 100;
    }
    for (let k = 0; k < touchedCount; k++) {
      const t = touched[k];
      // Tint, premultiplied by the tinted share so filtering blends right.
      for (let c = 0; c < 4; c++) tint[t * 4 + c] = Math.min(255, (tintSum[t * 4 + c] / cAll[t]) * 255);
      // Mean ammo, for the overview's saturation.
      st[t * 4] = Math.min(255, (ammoSum[t] / cAll[t]) * 2.55);
      st[t * 4 + 1] = (cSupp[t] / cAll[t]) * 255;
      // Mean health, for the overview's saturation.
      st[t * 4 + 2] = Math.min(255, (hpSum[t] / cAll[t]) * 2.55);
    }
    // Deaths leave a fading glow.
    if (jump) {
      for (const t of this.heatTiles) this.heat[t] = 0;
      this.heatTiles.clear();
      this.effects = [];
    }
    for (const t of this.heatTiles) {
      this.heat[t] *= 0.8;
      if (this.heat[t] < 0.02) {
        this.heat[t] = 0;
        this.heatTiles.delete(t);
      }
    }
    for (const d of s.deaths) {
      this.heat[d.tile] = Math.min(1, this.heat[d.tile] + 0.5);
      this.heatTiles.add(d.tile);
    }
    for (const t of this.heatTiles) st[t * 4 + 3] = this.heat[t] * 255;
    // Keep the previous frame for crossfading (on a seek, nothing to fade).
    const g = this.ground;
    const box = this.dirty;
    const grow = (t: number) => {
      const q = t % w;
      const r = (t / w) | 0;
      if (q < box[0]) box[0] = q;
      if (r < box[1]) box[1] = r;
      if (q > box[2]) box[2] = q;
      if (r > box[3]) box[3] = r;
    };
    for (let k = 0; k < touchedCount; k++) grow(touched[k]);
    for (const t of this.heatTiles) grow(t);
    // A seek (or the first frame): this frame's hexes and wherever the
    // textures may still hold an earlier frame's (the ground's army box),
    // not the whole map: a 2048-hex map's textures are 16 MB each.
    if (jump) {
      const cur: [number, number, number, number] = [box[0], box[1], box[2], box[3]];
      const a = g.armyBox;
      box[0] = Math.min(box[0], a[0]);
      box[1] = Math.min(box[1], a[1]);
      box[2] = Math.max(box[2], a[2]);
      box[3] = Math.max(box[3], a[3]);
      g.armyBox = cur;
    } else {
      const a = g.armyBox;
      g.armyBox = [Math.min(a[0], box[0]), Math.min(a[1], box[1]), Math.max(a[2], box[2]), Math.max(a[3], box[3])];
    }
    const none = box[0] > box[2];
    const [x, y, bw, bh] = none ? [0, 0, 0, 0] : [box[0], box[1], box[2] - box[0] + 1, box[3] - box[1] + 1];
    if (jump) this.dirty = [Infinity, Infinity, -Infinity, -Infinity];
    [g.occ, g.occPrev] = [g.occPrev, g.occ];
    [g.status, g.statusPrev] = [g.statusPrev, g.status];
    [g.tint, g.tintPrev] = [g.tintPrev, g.tint];
    g.occ.uploadRect(occ, x, y, bw, bh);
    if (mine >= 0) {
      [g.own, g.ownPrev] = [g.ownPrev, g.own];
      g.own.uploadRect(own, x, y, bw, bh);
      if (jump) g.ownPrev.uploadRect(own, x, y, bw, bh);
    }
    g.status.uploadRect(st, x, y, bw, bh);
    g.tint.uploadRect(tint, x, y, bw, bh);
    if (jump) {
      g.occPrev.uploadRect(occ, x, y, bw, bh);
      g.statusPrev.uploadRect(st, x, y, bw, bh);
      g.tintPrev.uploadRect(tint, x, y, bw, bh);
      // The box again from this frame on (the seek's own hexes included).
      for (let k = 0; k < touchedCount; k++) grow(touched[k]);
    }
    if (s.corridorVersion !== this.corridorsAt) {
      this.corridorsAt = s.corridorVersion;
      this.ground.setCorridors(s.corridor);
    }
    this.updateSight(jump);

    // Effects: shell impacts and deaths, timed in game ticks.
    if (!jump) {
      for (const sh of s.impacts) {
        const q = sh.tile % w;
        const r = (sh.tile / w) | 0;
        this.effects.push({ x: hexX(q, r), y: hexY(r), tick: sh.impact, kind: "burst", faction: 0 });
      }
      for (const d of s.deaths) {
        const q = d.tile % w;
        const r = (d.tile / w) | 0;
        this.effects.push({ x: hexX(q, r), y: hexY(r), tick: s.tick, kind: "death", faction: d.faction });
      }
    }
    const horizon = s.tick - s.header.frame_interval * 6;
    this.effects = this.effects.filter((e) => e.tick >= horizon);
  }

  /** Units on a tile (row-major index), in id order. */
  unitsOn(tile: number): number[] {
    const out: number[] = [];
    for (let id = this.head[tile]; id !== -1 && id !== undefined; id = this.next[id]) out.push(id);
    return out.reverse();
  }

  /** Interpolated world position of a unit. */
  unitPos(id: number, alpha: number): [number, number] {
    return [this.prevX[id] + (this.posX[id] - this.prevX[id]) * alpha, this.prevY[id] + (this.posY[id] - this.prevY[id]) * alpha];
  }

  pick(sx: number, sy: number, alpha: number): Pick {
    const [wx, wy] = this.camera.toWorld(sx, sy);
    const [q, r] = hexAt(wx, wy);
    const s = this.state;
    const inside = q >= 0 && r >= 0 && q < s.width && r < s.height;
    const tile = inside ? r * s.width + q : null;
    let unit: number | null = null;
    if (inside && this.detail() > 0.2) {
      let best = Infinity;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dq = -1; dq <= 1; dq++) {
          const nq = q + dq;
          const nr = r + dr;
          if (nq < 0 || nr < 0 || nq >= s.width || nr >= s.height) continue;
          for (const id of this.unitsOn(nr * s.width + nq)) {
            if (!shows(this.options, s.faction[id])) continue;
            const [x, y] = this.unitPos(id, alpha);
            const d = Math.hypot(x - wx, y - wy);
            const reach = Math.max(this.radii[s.design[id]] * 1.3, 6 / this.camera.zoom);
            if (d < reach && d < best) {
              best = d;
              unit = id;
            }
          }
        }
      }
    }
    return { q, r, tile, unit };
  }

  /** 0 when zoomed out (armies as colour), 1 when hexes are big enough for unit sprites. */
  detail(): number {
    const px = this.camera.zoom * SQRT3;
    return Math.max(0, Math.min(1, (px - 5) / 7));
  }

  /** When the breach playing started (render time), if one is. */
  private breachStart: number | null = null;

  /** A breach of the storm wall at hex `at`: the regions animate from
   *  `before` to the world's (a few seconds). */
  breach(before: Uint8Array, at: [number, number]): void {
    this.ground.breach(before, at);
    this.breachStart = null;
  }

  render(time: number, alpha: number): void {
    const gl = this.gl;
    const cam = this.camera;
    const s = this.state;
    const o = this.options;
    if (this.ground.breachT >= 0) {
      this.breachStart ??= time;
      this.ground.breachT = time - this.breachStart > 3.5 ? -1 : time - this.breachStart;
    }
    const detail = this.detail();
    if (this.findings && this.oreShown !== o.ore) this.buildOre();
    gl.viewport(0, 0, cam.width, cam.height);
    gl.disable(gl.BLEND);
    let layers = 0;
    if (o.track) layers |= Layer.TRACK;
    if (o.corridors) layers |= Layer.CORRIDORS;
    if (o.zones) layers |= Layer.ZONES;
    if (o.grid) layers |= Layer.GRID;
    const hover: [number, number] = this.hover ? [this.hover.q, this.hover.r] : [-1, -1];
    this.ground.draw(
      {
        center: [cam.x, cam.y],
        zoom: cam.zoom,
        viewport: [cam.width, cam.height],
        detail,
        mode: o.units ? OverviewMode.Armies : OverviewMode.Terrain,
        sat: SAT_CHANNEL[o.sat],
        layers,
        corridorMask: o.factions,
        hover,
        blend: alpha,
        fog: o.sight + 1,
        fancy: settings.terrainDetail,
        storm: true,
        time,
        territory: o.territory,
        ore: o.intel,
      },
      this.factionColors.map((c, i) => (shows(o, i) ? c : ([0, 0, 0] as [number, number, number]))),
      [0, 0, cam.width, cam.height],
    );

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    const [bx0, by0, bx1, by1] = cam.bounds();
    const pad = 2;
    const visible = (x: number, y: number) => x > bx0 - pad && x < bx1 + pad && y > by0 - pad && y < by1 + pad;
    const fi = s.header.frame_interval;
    const tickNow = s.frame <= 0 ? s.tick : s.tick - (1 - alpha) * fi;
    const center: [number, number] = [cam.x, cam.y];
    const viewport: [number, number] = [cam.width, cam.height];

    // Fire lines under the units.
    this.lines.begin();
    if (o.fire && o.units && detail > 0.25) {
      for (const id of s.live) {
        const t = s.target[id];
        // Indirect fire shows as shells, not as lines to a target.
        if (t === NO_ID || !s.alive[t] || !shows(o, s.faction[id]) || this.designs[s.design[id]].indirect) continue;
        const [x0, y0] = this.unitPos(id, alpha);
        const [x1, y1] = this.unitPos(t, alpha);
        if (!visible(x0, y0) && !visible(x1, y1)) continue;
        const c = this.factionColors[s.faction[id]] ?? [1, 1, 1];
        this.lines.push(x0, y0, x1, y1, c, 0.55 * detail, 1.6, LineStyle.Tracer);
      }
    }
    // Build beams: from each engineer working a site (beside it, and the
    // site not stuck) to the site, in its side's colour.
    if (o.units && detail > 0.25 && this.sites.length) {
      for (const id of s.live) {
        if (!(this.parts[s.design[id]] & Part.BUILDER)) continue;
        const [uq, ur] = [s.q(s.tile[id]), s.r(s.tile[id])];
        for (const site of this.sites) {
          if (site.owner !== s.faction[id] || site.blocked || site.queued) continue;
          // Beside it, or beside a track's far end.
          const near = (p: [number, number]) => Math.max(Math.abs(p[0] - uq), Math.abs(p[1] - ur), Math.abs(p[0] - uq + p[1] - ur)) <= 1;
          const end = near(site.at) ? site.at : site.what === "track" && site.to && near(site.to) ? site.to : null;
          if (!end) continue;
          const [x0, y0] = this.unitPos(id, alpha);
          const [x1, y1] = [hexX(end[0], end[1]), hexY(end[1])];
          if (!visible(x0, y0) && !visible(x1, y1)) continue;
          this.lines.push(x0, y0, x1, y1, this.factionColors[site.owner] ?? [1, 1, 1], 0.9, 1.6, LineStyle.Tracer);
          break;
        }
      }
    }
    // Shell arcs.
    if (o.shells) {
      for (const sh of s.shells) {
        const [fx, fy] = this.tileXY(sh.from);
        const [tx, ty] = this.tileXY(sh.tile);
        if (!visible(fx, fy) && !visible(tx, ty)) continue;
        this.lines.push(fx, fy, tx, ty, [1, 0.45, 0.3], 0.18, 1, LineStyle.Dashed);
      }
    }
    this.lines.draw(center, cam.zoom, viewport, time);

    // Structures: their own batch, kept a readable size at any zoom.
    const st = this.structures;
    st.begin();
    if (o.depots) {
      const team = (f: number): RGB => this.factionColors[f] ?? [0.9, 0.9, 0.9];
      s.statics.producers.forEach((p, k) => {
        if (p.faction !== o.sight && !this.known(p.tile)) return;
        const [x, y] = this.tileXY(p.tile);
        const waiting = s.barracks.get(k)?.waiting ? SpriteFlag.DRY : 0;
        if (visible(x, y)) st.push(x, y, 0.95, this.foundries.has(p.tile) ? Shape.Foundry : Shape.Producer, team(p.faction), 1, 1, waiting);
      });
      // Each structure by its kind (one structure, one glyph): a mine or
      // plant fills as it's built, greys when idle, reddens below working
      // order; a barracks is drawn as its producer, above.
      const producerAt = new Set(s.statics.producers.map((p) => p.tile));
      const atStation = this.stationSites ??= sitesByStation(s.header);
      s.depots.forEach((d, k) => {
        // Closed (255): gone, unless being taken down (grey till then).
        const closed = d.faction === 255;
        if (closed && !this.decommissioning.has(`${s.q(d.tile)},${s.r(d.tile)}`)) return;
        if (!closed && d.faction !== o.sight && !this.known(d.tile)) return;
        if (d.kind === DepotKind.Barracks && producerAt.has(d.tile)) return;
        const [x, y] = this.tileXY(d.tile);
        if (!visible(x, y)) return;
        const fill = d.capacity ? d.stock / d.capacity : 0;
        if (d.kind === DepotKind.Mine || d.kind === DepotKind.Plant) {
          const i = atStation.get(k)?.[0];
          const info = i === undefined ? undefined : s.header.sites?.[i];
          const now = i === undefined ? undefined : s.sites[i];
          const built = info && now ? now.integrity / Math.max(1, info.full) : 1;
          const working = !info || built * 100 >= info.operational_pct;
          const idle = i !== undefined && !!industry.stationIdle(k, s);
          const shape = d.kind === DepotKind.Mine ? Shape.MineWorks : Shape.PlantWorks;
          st.push(x, y, 0.95, shape, working ? team(d.faction) : [1, 0.32, 0.26], 1, built, idle ? SpriteFlag.DRY : 0);
          return;
        }
        const shape = d.kind === DepotKind.Home ? Shape.Home : d.kind === DepotKind.Station ? Shape.Station : d.kind === DepotKind.Barracks ? Shape.Producer : Shape.Depot;
        // Home the largest; a field depot small. Freight stations (no
        // munitions store) show empty.
        const size = d.kind === DepotKind.Home ? 1.2 : d.field ? 0.75 : 0.95;
        st.push(x, y, size, shape, team(d.faction), 1, fill, d.field ? SpriteFlag.FIELD : 0);
      });
    }
    // Construction sites: a ghost of what each will be, in its builder's
    // colour, see-through and filling as it's built; track, its line.
    const tracks: [number, number, number, number, readonly [number, number, number]][] = [];
    for (const site of this.sites) {
      const [x, y] = [hexX(site.at[0], site.at[1]), hexY(site.at[1])];
      const own = this.factionColors[site.owner] ?? ([0.9, 0.9, 0.9] as RGB);
      // Queued (past the open sites' limit): fainter, waiting its turn.
      const colour = site.queued ? (own.map((v) => v * 0.55) as unknown as RGB) : own;
      const done = Math.min(1, siteDone(site) / Math.max(1, site.need));
      if (site.what === "track" && site.to) {
        tracks.push([x, y, hexX(site.to[0], site.to[1]), hexY(site.to[1]), colour]);
        continue;
      }
      if (!visible(x, y)) continue;
      const shape =
        site.what === "mine" ? Shape.Mine : site.what === "plant" ? Shape.Plant : site.what === "barracks" ? Shape.Producer : site.what === "foundry" ? Shape.Foundry : site.what === "station" || site.what === "trade_post" ? Shape.Station : Shape.Depot;
      // Queued: a faint ghost (Factorio's, waiting for robots).
      st.push(x, y, 0.95, shape, colour, site.queued ? 0.28 : 0.35 + 0.4 * done, site.queued ? 0 : done);
    }
    // A readable size, but not overlapping when zoomed far out.
    st.draw(center, cam.zoom, viewport, time, Math.min(16, Math.max(5, cam.zoom * 5)));
    if (tracks.length) {
      this.lines.begin();
      for (const [x0, y0, x1, y1, c] of tracks) this.lines.push(x0, y0, x1, y1, c, 0.6, 2, LineStyle.Dashed);
      this.lines.draw(center, cam.zoom, viewport, time);
    }

    // Units.
    this.units.begin();
    this.emplaced = [];
    if (o.units && detail > 0) {
      for (const id of s.live) {
        const f = s.faction[id];
        if (!shows(o, f)) continue;
        if (f !== o.sight && !this.inSight(s.tile[id])) continue;
        const [x, y] = this.unitPos(id, alpha);
        if (!visible(x, y)) continue;
        const d = s.design[id];
        const info = this.designs[d];
        let flags = 0;
        if (info.damage > 0 && s.ammo[id] === 0) flags |= SpriteFlag.DRY;
        if (s.suppression[id] >= 5) flags |= SpriteFlag.SUPPRESSED;
        if (s.dug[id] >= 12) flags |= SpriteFlag.DUG_IN;
        if (s.flags[id] & UnitFlags.CONVOY) flags |= SpriteFlag.CONVOY;
        if (s.flags[id] & (UnitFlags.ENGINE_OUT | UnitFlags.WEAPON_OUT)) flags |= SpriteFlag.KNOCKED_OUT;
        if (s.flags[id] & UnitFlags.RETURNING) flags |= SpriteFlag.RETURNING;
        if (s.cargo[id] > 0) flags |= SpriteFlag.LOADED;
        if (id === this.selected || this.selection?.has(id)) flags |= SpriteFlag.SELECTED;
        // Face the direction of travel (vehicles).
        if (info.footprint) continue;
        // A comms post or training camp (v31.1: an emplacement unit): drawn
        // as its structure's mark on the overlay, not as a unit.
        const em = this.emplacementOf(d);
        if (em) {
          this.emplaced.push({ id, kind: em, x, y, faction: f });
          continue;
        }
        const angle = this.shapes[d] === Shape.Truck || this.shapes[d] === Shape.Tank ? this.heading(id) : 0;
        const colour = this.unitColour(id, info, f);
        this.units.push(x, y, this.radii[d], this.shapes[d], colour, detail, s.hp[id] / Math.max(1, info.hp), flags, (f + 1) / 255, angle, this.parts[d]);
      }
    }
    this.units.setFactions(this.factionColors.map((c, i) => (shows(o, i) ? c : ([0, 0, 0] as RGB))));
    this.units.draw(center, cam.zoom, viewport, time, 2.5);

    // Titans: on their own canvas, in world units, clear of the console.
    const tc = this.titanCtx;
    tc.setTransform(1, 0, 0, 1, 0, 0);
    tc.clearRect(0, 0, cam.width, cam.height);
    tc.save();
    tc.beginPath();
    tc.rect(0, cam.insets.top, cam.width, Math.max(0, cam.height - cam.insets.top - cam.insets.bottom));
    tc.clip();
    tc.setTransform(cam.zoom, 0, 0, cam.zoom, cam.width / 2 - cam.x * cam.zoom, cam.height / 2 - cam.y * cam.zoom);
    const drawn = new Set<number>();
    // At every zoom (a titan is big enough to see from far out), and while
    // any of it is on screen, not just its centre.
    if (o.units) {
      for (const id of s.live) {
        const fp = this.designs[s.design[id]].footprint;
        if (!fp) continue;
        const f = s.faction[id];
        if (!shows(o, f) || (f !== o.sight && !this.inSight(s.tile[id]))) continue;
        const [x, y] = this.unitPos(id, alpha);
        const margin = 12;
        if (!(x > bx0 - margin && x < bx1 + margin && y > by0 - margin && y < by1 + margin)) continue;
        const t = s.target[id];
        const aim = t !== NO_ID && s.alive[t] ? this.unitPos(t, alpha) : null;
        tc.globalAlpha = 1;
        this.titans.draw(tc, id, fp, x, y, aim, this.factionColors[f] ?? [1, 1, 1], time, 1 / cam.zoom);
        // Selected: a ring round it.
        if (id === this.selected || this.selection?.has(id)) {
          const reach = Math.max(...fp.tiles.map(([q, r]) => Math.hypot(SQRT3 * (q + r / 2), 1.5 * r))) + 1.6;
          tc.strokeStyle = "rgba(255,255,255,0.85)";
          tc.lineWidth = 2 / cam.zoom;
          const [bx, by] = this.titans.drawnAt(id) ?? [x, y];
          tc.beginPath();
          tc.arc(bx, by, reach, 0, Math.PI * 2);
          tc.stroke();
        }
        drawn.add(id);
      }
    }
    tc.globalAlpha = 1;
    tc.restore();
    this.titans.endFrame(time, drawn);

    // Markers, trains, shells, effects: visible at every zoom.
    const ov = this.overlay;
    ov.begin();
    if (o.depots) {
      // Industry: mines and plants around their station, in the owner's
      // colour, filled by how built they are; red below working order.
      const sites = s.header.sites ?? [];
      for (let i = 0; i < sites.length; i++) {
        const site = sites[i];
        const st = s.sites[i];
        if (!st) continue;
        // A mine's or plant's own depot is its glyph, drawn above.
        const kind = s.depots[site.station]?.kind;
        if (kind === DepotKind.Mine || kind === DepotKind.Plant) continue;
        const [cx, cy] = this.tileXY(site.tile);
        const same = sites.filter((o) => o.tile === site.tile);
        const k = same.indexOf(site);
        const angle = (k / Math.max(1, same.length)) * Math.PI * 2 - Math.PI / 2;
        const [x, y] = [cx + Math.cos(angle) * 1.25, cy + Math.sin(angle) * 1.25];
        if (!visible(x, y)) continue;
        const built = st.integrity / Math.max(1, site.full);
        const team = this.factionColors[site.faction] ?? ([1, 1, 1] as RGB);
        const working = built * 100 >= site.operational_pct;
        const color: RGB = working ? team : [1, 0.32, 0.26];
        ov.push(x, y, 0.95, site.kind === "mine" ? Shape.Mine : Shape.Plant, color, 1, built);
      }
    }
    if (o.trains) {
      for (const t of s.trains) {
        const prev = s.prevTrains.get(t.id) ?? t.tile;
        const [x0, y0] = this.tileXY(prev);
        const [x1, y1] = this.tileXY(t.tile);
        const x = x0 + (x1 - x0) * alpha;
        const y = y0 + (y1 - y0) * alpha;
        if (!visible(x, y)) continue;
        const angle = x1 !== x0 || y1 !== y0 ? Math.atan2(y1 - y0, x1 - x0) : 0;
        // Troops in their side's colour, freight rust, munitions grey.
        const c: [number, number, number] =
          t.flags & TrainFlags.TROOPS
            ? (this.factionColors[t.faction] ?? [1, 1, 1])
            : t.flags & TrainFlags.FREIGHT
              ? [0.72, 0.42, 0.24]
              : [0.55, 0.55, 0.55];
        ov.push(x, y, 0.8, Shape.Train, c, 1, 1, 0, 0, angle);
      }
    }
    if (o.shells) {
      const flight = Math.max(1, ...this.designs.map((d) => d.flight ?? (d.indirect ? 15 : 0)));
      for (const sh of s.shells) {
        const [fx, fy] = this.tileXY(sh.from);
        const [tx, ty] = this.tileXY(sh.tile);
        const p = Math.max(0, Math.min(1, 1 - (sh.impact - tickNow) / flight));
        const lift = Math.sin(p * Math.PI) * Math.hypot(tx - fx, ty - fy) * 0.18;
        const x = fx + (tx - fx) * p;
        const y = fy + (ty - fy) * p - lift;
        if (visible(x, y)) ov.push(x, y, 0.35, Shape.Projectile, [1, 0.7, 0.3], 1);
        if (visible(tx, ty)) ov.push(tx, ty, 0.6 + 0.4 * (1 - p), Shape.TargetRing, [1, 0.35, 0.25], 0.5 * p + 0.2);
      }
    }
    if (o.effects && settings.effects) {
      for (const e of this.effects) {
        if (!visible(e.x, e.y)) continue;
        if (e.kind === "burst") {
          const t = (tickNow - e.tick) / (fi * 2);
          if (t >= 0 && t < 1) ov.push(e.x, e.y, 1.1, Shape.Burst, [1, 0.5, 0.15], 1, 1, 0, t);
        } else if (detail > 0.3) {
          const t = (tickNow - e.tick + fi) / (fi * 6);
          if (t >= 0 && t < 1) ov.push(e.x, e.y, 0.3, Shape.Death, [0.9, 0.25, 0.2], detail, 1, 0, t);
        }
      }
    }
    // What a left-click at the cursor would pick (the same pick as the
    // click): a faint ring on the unit, else round the structure.
    const h = this.hover;
    if (h && h.unit !== null && s.alive[h.unit] && h.unit !== this.selected && !this.selection?.has(h.unit)) {
      const [x, y] = this.unitPos(h.unit, alpha);
      ov.push(x, y, 0.7, Shape.TargetRing, [1, 1, 1], 0.6);
    } else if (h && h.unit === null && h.tile !== null && (s.depots.some((d) => d.tile === h.tile && d.faction !== 255) || s.statics.producers.some((p) => p.tile === h.tile))) {
      const [x, y] = this.tileXY(h.tile);
      ov.push(x, y, 1.3, Shape.TargetRing, [1, 1, 1], 0.6);
    }
    if (this.selected !== null && s.alive[this.selected]) {
      const [x, y] = this.unitPos(this.selected, alpha);
      ov.push(x, y, 0.75, Shape.Selection, [1, 1, 1], 1);
      const t = this.designs[s.design[this.selected]].indirect ? NO_ID : s.target[this.selected];
      if (t !== NO_ID && s.alive[t]) {
        const [tx, ty] = this.unitPos(t, alpha);
        ov.push(tx, ty, 0.6, Shape.TargetRing, [1, 0.3, 0.25], 0.9);
      }
    }
    ov.draw(center, cam.zoom, viewport, time, 3);

    this.clearMinimapSlot();
    if (o.minimap) this.drawMinimap(time);
  }

  /** A unit's fill: its team colour, tinted toward its state's hue, faded by
   *  the analog measure. */
  private unitColour(id: number, info: DesignInfo, f: number): RGB {
    const s = this.state;
    const o = this.options;
    const team = this.factionColors[f] ?? ([1, 1, 1] as RGB);
    const th = tintOf(s, info, id, o.mark, this.kinds[s.design[id]]);
    const c = th ? mixRGB(team, th, TINT) : team;
    return o.sat === "none" ? c : saturate(c, metric(s, info, id, o.sat));
  }

  private heading(id: number): number {
    const dx = this.posX[id] - this.prevX[id];
    const dy = this.posY[id] - this.prevY[id];
    if (Math.abs(dx) + Math.abs(dy) < 1e-3) return this.lastAngle.get(id) ?? 0;
    const a = Math.atan2(dy, dx);
    this.lastAngle.set(id, a);
    return a;
  }
  private lastAngle = new Map<number, number>();

  tileXY(tile: number): [number, number] {
    const w = this.state.width;
    const q = tile % w;
    const r = (tile / w) | 0;
    return [hexX(q, r), hexY(r)];
  }

  /** Minimap rectangle in device pixels (x, y from bottom-left for GL, and top-left for DOM). */
  minimapRect(): { x: number; y: number; w: number; h: number; top: number } {
    const dpr = this.dpr;
    const [x0, y0, x1, y1] = this.worldBounds;
    const aspect = (y1 - y0) / (x1 - x0);
    const slot = this.minimapSlot;
    if (!slot) {
      // No console slot: the bottom right corner.
      const w = Math.round(MINIMAP.width * dpr);
      const h = Math.round(w * aspect);
      const m = Math.round(MINIMAP.margin * dpr);
      return { x: this.camera.width - w - m, y: m, w, h, top: this.camera.height - m - h };
    }
    // The map's shape, as large as fits in the console's slot.
    const pad = 6;
    const sw = (slot.width - 2 * pad) * dpr;
    const sh = (slot.height - 2 * pad) * dpr;
    const w = Math.round(Math.min(sw, sh / aspect));
    const h = Math.round(w * aspect);
    const x = Math.round((slot.left + pad) * dpr + (sw - w) / 2);
    const top = Math.round((slot.top + pad) * dpr + (sh - h) / 2);
    return { x, y: this.camera.height - top - h, w, h, top };
  }

  /** Minimap scale and centre (device pixels per world unit). */
  minimapView(): { zoom: number; cx: number; cy: number } {
    const r = this.minimapRect();
    const [x0, y0, x1, y1] = this.worldBounds;
    return { zoom: r.w / (x1 - x0), cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
  }

  /** The console's minimap slot: the panel's colour, not the map under it. */
  private clearMinimapSlot(): void {
    const slot = this.minimapSlot;
    if (!slot) return;
    const gl = this.gl;
    const dpr = this.dpr;
    const top = Math.round(slot.top * dpr);
    const sh = Math.round(slot.height * dpr);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(Math.round(slot.left * dpr), this.camera.height - top - sh, Math.round(slot.width * dpr), sh);
    gl.clearColor(0x15 / 255, 0x15 / 255, 0x14 / 255, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.disable(gl.SCISSOR_TEST);
  }

  private drawMinimap(time: number): void {
    const gl = this.gl;
    const r = this.minimapRect();
    const v = this.minimapView();
    const o = this.options;
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(r.x, r.y, r.w, r.h);
    gl.viewport(r.x, r.y, r.w, r.h);
    gl.disable(gl.BLEND);
    this.ground.draw(
      {
        center: [v.cx, v.cy],
        zoom: v.zoom,
        viewport: [r.w, r.h],
        detail: 0,
        mode: o.units ? OverviewMode.Armies : OverviewMode.Terrain,
        sat: SAT_CHANNEL[o.sat],
        layers: 0,
        corridorMask: 0,
        hover: [-1, -1],
        blend: 1,
        fog: o.sight + 1,
        // Off the map: the console's colour, so the map sits in its slot unframed.
        void: [0x15 / 255, 0x15 / 255, 0x14 / 255],
      },
      this.factionColors.map((c, i) => (shows(o, i) ? c : ([0, 0, 0] as [number, number, number]))),
      [r.x, r.y, r.w, r.h],
    );
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    // Structures: a small plate each in its owner's colour (BAR's minimap
    // marks buildings), where seen.
    if (o.depots) {
      const s = this.state;
      const st = this.structures;
      st.begin();
      const team = (f: number): RGB => this.factionColors[f] ?? [0.9, 0.9, 0.9];
      for (const d of s.depots) {
        // Field depots trail the fronts in their dozens: not marked.
        if (d.field || !shows(o, d.faction) || (d.faction !== o.sight && !this.known(d.tile))) continue;
        const [x, y] = this.tileXY(d.tile);
        st.push(x, y, 0.95, Shape.Depot, team(d.faction), 1, 1);
      }
      for (const p of s.statics.producers) {
        if (!shows(o, p.faction) || (p.faction !== o.sight && !this.known(p.tile))) continue;
        const [x, y] = this.tileXY(p.tile);
        st.push(x, y, 0.95, this.foundries.has(p.tile) ? Shape.Foundry : Shape.Producer, team(p.faction), 1, 1);
      }
      st.draw([v.cx, v.cy], v.zoom, [r.w, r.h], time, 3.5);
    }
    // The camera's view.
    const [bx0, by0, bx1, by1] = this.camera.bounds();
    this.lines.begin();
    const white: [number, number, number] = [1, 1, 1];
    this.lines.push(bx0, by0, bx1, by0, white, 0.9, 1.5);
    this.lines.push(bx1, by0, bx1, by1, white, 0.9, 1.5);
    this.lines.push(bx1, by1, bx0, by1, white, 0.9, 1.5);
    this.lines.push(bx0, by1, bx0, by0, white, 0.9, 1.5);
    this.lines.draw([v.cx, v.cy], v.zoom, [r.w, r.h], time);
    gl.disable(gl.SCISSOR_TEST);
    gl.viewport(0, 0, this.camera.width, this.camera.height);
  }
}
